/**
 * Inline SVG.
 *
 * An icon font or a sprite sheet would be a second request and a second thing
 * to cache-bust; there are a dozen icons and they compress to nothing.
 */

import type { ComponentChildren } from "preact";

interface Props {
  class?: string;
}

function svg(path: ComponentChildren, viewBox = "0 0 24 24") {
  return function Icon({ class: cls = "icon" }: Props) {
    return (
      <svg
        class={cls}
        viewBox={viewBox}
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };
}

export const Logo = svg(
  <>
    <path d="M4 6.5h16v11H4z" />
    <path d="M4 7.5l8 6 8-6" />
  </>,
);

export const Inbox = svg(
  <>
    <path d="M4 13h4l1.5 2.5h5L16 13h4" />
    <path d="M5.5 5h13l1.5 8v4.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 2 17.5V13z" />
  </>,
);

export const Star = svg(<path d="M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8z" />);

export const Send = svg(
  <>
    <path d="M21 4L3 11l7 3 3 7z" />
    <path d="M21 4l-11 10" />
  </>,
);

export const Archive = svg(
  <>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
  </>,
);

export const Trash = svg(
  <>
    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
  </>,
);

export const Folder = svg(
  <path d="M3 7a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />,
);

export const Search = svg(
  <>
    <circle cx="11" cy="11" r="6" />
    <path d="M16 16l4 4" />
  </>,
);

export const Pencil = svg(
  <>
    <path d="M4 20l4-1 10-10-3-3L5 16z" />
    <path d="M14 6l3 3" />
  </>,
);

// Sliders rather than a cog: at 17px a cog and the sun of the theme toggle
// are the same smudge, and the two sit next to each other.
export const Gear = svg(
  <>
    <path d="M4 7h9M17 7h3M4 12h3M11 12h9M4 17h9M17 17h3" />
    <circle cx="15" cy="7" r="2" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="15" cy="17" r="2" />
  </>,
);

export const Users = svg(
  <>
    <circle cx="9" cy="9" r="3" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 7.2a3 3 0 0 1 0 5.6M17 19a5.6 5.6 0 0 0-2-4.3" />
  </>,
);

export const Refresh = svg(
  <>
    <path d="M20 12a8 8 0 1 1-2.6-5.9" />
    <path d="M20 4v4h-4" />
  </>,
);

export const Close = svg(<path d="M6 6l12 12M18 6L6 18" />);

export const Back = svg(<path d="M15 5l-7 7 7 7" />);

/*
 * The three window buttons.
 *
 * Drawn to the same 24-box and the same 1.8 stroke as everything else here, so
 * they sit level with Refresh and the filter chevron in the same row -- window
 * controls that are visibly a different weight from the app's own icons read as
 * borrowed rather than as part of the app.
 *
 * `Close` above is reused for the third: a cross is a cross, and a second one
 * drawn slightly differently would be the kind of near-miss nobody can name but
 * everybody sees.
 */
export const Minimize = svg(<path d="M6 12h12" />);

export const Maximize = svg(<path d="M6 6h12v12H6z" />);

/** Two offset squares: the window is maximised and this puts it back. */
export const Restore = svg(
  <>
    <path d="M8 10h8v8H8z" />
    <path d="M10 8V6h8v8h-2" />
  </>,
);

/**
 * Points right. Rotated by CSS when the thing it opens is open, rather than
 * shipping a second down-pointing path: one glyph that turns is what makes the
 * two states read as the same control in two positions.
 */
export const Chevron = svg(<path d="M9 5l7 7-7 7" />);

export const Reply = svg(
  <>
    <path d="M9 7L4 12l5 5" />
    <path d="M4 12h9a6 6 0 0 1 6 6v1" />
  </>,
);

/**
 * Reply, with a second arrowhead for the rest of the recipients.
 *
 * Deliberately the same stem and curve as {@link Reply} with one mark added,
 * rather than a different drawing: these two sit side by side and the whole
 * question at a glance is "is this the one that includes everybody". A pair
 * that differed in outline would have to be read; a pair that differs by one
 * arrowhead can be told apart at icon size.
 */
export const ReplyAll = svg(
  <>
    <path d="M8 7L3 12l5 5" />
    <path d="M13 7l-5 5 5 5" />
    <path d="M8 12h7a6 6 0 0 1 6 6v1" />
  </>,
);

/**
 * {@link Reply}, mirrored: the same curve leaving the other way.
 *
 * A mirror rather than a new drawing for the same reason {@link ReplyAll} is
 * one mark added. The three sit in a row, and what a glance has to tell apart
 * is direction -- back to the sender, or on to somebody else -- which a
 * reflection says and a different shape would only obscure.
 */
export const Forward = svg(
  <>
    <path d="M15 7l5 5-5 5" />
    <path d="M20 12h-9a6 6 0 0 0-6 6v1" />
  </>,
);

export const Paperclip = svg(
  <path d="M19 10l-8.5 8.5a4 4 0 0 1-5.7-5.7L13 4.6a2.8 2.8 0 0 1 4 4l-8.4 8.4a1.6 1.6 0 0 1-2.3-2.3L14 6.9" />,
);

/** The printer: body, the paper feeding out of it, and the sheet on top. */
export const Printer = svg(
  <>
    <path d="M6 9V3h12v6" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="7" rx="1" />
  </>,
);

/** The funnel: the filter dropdown's trigger, and the empty state it explains. */
export const Filter = svg(<path d="M3.5 5h17l-6.6 7.6V19l-3.8 2v-8.4z" />);

export const Calendar = svg(
  <>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 9.5h17M8 3.5v3m8-3v3" />
  </>,
);

export const Sun = svg(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
  </>,
);

export const Moon = svg(<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />);

export const Plus = svg(<path d="M12 5v14M5 12h14" />);

export const Warning = svg(
  <>
    <path d="M12 4l9 16H3z" />
    <path d="M12 10v4M12 17h.01" />
  </>,
);

export const Check = svg(<path d="M5 12.5l4.5 4.5L19 7" />);

/**
 * A picture, for the one control that offers to fetch the pictures.
 *
 * The frame, a sun and the hill every photo icon has drawn since the first
 * one -- deliberately the most conventional shape available, because this
 * button appears only on messages that are missing something and has to be
 * recognised without a label beside it.
 */
export const Image = svg(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="8.5" cy="10" r="1.5" />
    <path d="M21 15l-5-4-5 4-2-1.5L3 18" />
  </>,
);

export const Bell = svg(
  <>
    <path d="M18 9a6 6 0 1 0-12 0c0 6-2.5 8-2.5 8h17S18 15 18 9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </>,
);

// A circle-slash rather than a bin: junk and trash are different destinations
// and sit next to each other, so they must not read as the same icon twice.
export const Junk = svg(
  <>
    <circle cx="12" cy="12" r="8" />
    <path d="M6.4 6.4l11.2 11.2" />
  </>,
);

/** Mark as read: an envelope with the flap open. */
export const MailOpen = svg(
  <>
    <path d="M3 10.5L12 4l9 6.5V19a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
    <path d="M3 10.5l9 6 9-6" />
  </>,
);

/** Mark as unread: the same envelope, sealed. */
export const MailClosed = svg(
  <>
    <rect x="3" y="5.5" width="18" height="13" rx="1" />
    <path d="M3 7l9 6 9-6" />
  </>,
);

export const More = svg(
  <>
    <circle cx="5" cy="12" r="1.1" />
    <circle cx="12" cy="12" r="1.1" />
    <circle cx="19" cy="12" r="1.1" />
  </>,
);

/** The way to the mailbox list where there is no room to keep it on screen. */
/**
 * Kept on this device: a tray with an arrow coming down into it.
 *
 * Deliberately not a pushpin. In every other mail client a pin means "hold this
 * at the top of the list", which is a statement about ordering; this one is
 * about where the bytes are. Borrowing the pushpin would promise the wrong
 * feature to anybody who has met the other one.
 */
export const KeepOffline = svg(
  <>
    <path d="M12 4v9" />
    <path d="M8.5 9.5L12 13l3.5-3.5" />
    <path d="M5 15v3.5h14V15" />
  </>,
);

export const Menu = svg(
  <>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </>,
);

// ---- the composer's formatting bar (ui/Editor.tsx) ----------------------------

export const Bold = svg(
  <>
    <path d="M7 5h6a3.5 3.5 0 0 1 0 7H7z" />
    <path d="M7 12h7a3.5 3.5 0 0 1 0 7H7z" />
  </>,
);

export const Italic = svg(
  <>
    <path d="M14 5h5" />
    <path d="M5 19h5" />
    <path d="M15 5l-6 14" />
  </>,
);

export const Underline = svg(
  <>
    <path d="M7 4v7a5 5 0 0 0 10 0V4" />
    <path d="M5 20h14" />
  </>,
);

export const Strike = svg(
  <>
    <path d="M4 12h16" />
    <path d="M16.5 7.5A4 4 0 0 0 12 5c-2.5 0-4.5 1.3-4.5 3.2 0 1.2.7 2 2 2.6" />
    <path d="M7.5 16.5A4 4 0 0 0 12 19c2.5 0 4.5-1.3 4.5-3.2 0-.6-.2-1.1-.5-1.5" />
  </>,
);

export const Link = svg(
  <>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.5 1.5" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5" />
  </>,
);

export const ListBullets = svg(
  <>
    <path d="M9 6h11" />
    <path d="M9 12h11" />
    <path d="M9 18h11" />
    <path d="M4.5 6h.01" />
    <path d="M4.5 12h.01" />
    <path d="M4.5 18h.01" />
  </>,
);

export const ListNumbers = svg(
  <>
    <path d="M10 6h10" />
    <path d="M10 12h10" />
    <path d="M10 18h10" />
    <path d="M4 5l1.5-1v5" />
    <path d="M3.8 14.2a1.4 1.4 0 0 1 2.6.7c0 .6-.5 1-1.1 1.6L3.8 18H6.6" />
  </>,
);

export const Quote = svg(
  <>
    <path d="M6 17h3l2-4V7H5v6h3z" />
    <path d="M14 17h3l2-4V7h-6v6h3z" />
  </>,
);

export const ClearFormat = svg(
  <>
    <path d="M5 5h12" />
    <path d="M11 5l-3 14" />
    <path d="M15 15l5 5" />
    <path d="M20 15l-5 5" />
  </>,
);
